import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import AlertNotificationBannerItem from "./alertNotificationBannerItem/AlertNotificationBannerItem.jsx";
import { ALERT_TYPES, NOTIFICATION_BANNER_PREF_CODE, REMINDER_STATE } from "../../../other/constants/OtherKeys.jsx";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import {ScrollMenu, VisibilityContext} from "react-horizontal-scrolling-menu";
import {BryntumButton} from "@bryntum/schedulerpro-react";
import {GRANTS} from "../../../other/constants/PrivilegeTypes.jsx";
import {getSavedBoardSelection} from "../../hooks/useBoards.jsx";
import {getIsArchivedForBoard} from "../../../other/utils/otherUtils.jsx";
import { asyncTaskCancel, markAsyncTaskAsSeen } from '../../../network/network.jsx';
import { processCompletedTask } from "../../../other/utils/asyncTaskUtils.jsx";
import BryntumContext from "../../context/bryntumContext.jsx";

const useDrag = () => {
  const [clicked, setClicked] = useState(false);
  const [dragging, setDragging] = useState(false);
  const position = useRef(0);

  const dragStart = useCallback((ev) => {
    position.current = ev.clientX;
    setClicked(true);
  }, []);

  const dragStop = useCallback(() =>
      // NOTE: need some delay so item under cursor won't be clicked
      window.requestAnimationFrame(() => {
        setDragging(false);
        setClicked(false);
      }),
    []);

  const dragMove = (ev, cb) => {
    const newDiff = position.current - ev.clientX;

    const movedEnough = Math.abs(newDiff) > 5;

    if (clicked && movedEnough) {
      setDragging(true);
    }

    if (dragging && movedEnough) {
      position.current = ev.clientX;
      cb(newDiff);
    }
  };

  return {
    dragStart,
    dragStop,
    dragMove
  };
}

const RightArrow = () => {
  const {isLastItemVisible, scrollNext} = useContext(VisibilityContext);

  return (
    <BryntumButton
      disabled={isLastItemVisible} icon="fa-solid fa-circle-chevron-right" cls="b-transparent border-none text-2xl py-0 px-2 min-h-0 text-muted-foreground hover:!text-muted-foreground hover:!bg-transparent"
      onClick={() => scrollNext()}
    />
  );
}

const LeftArrow = () => {
  const {isFirstItemVisible, scrollPrev} = useContext(VisibilityContext);

  return (
    <BryntumButton
      disabled={isFirstItemVisible} icon="fa-solid fa-circle-chevron-left" cls="b-transparent border-none text-2xl py-0 px-2 min-h-0 text-muted-foreground hover:!text-muted-foreground hover:!bg-transparent"
      onClick={() => scrollPrev()}/>
  );
}

const ScrollMenuWrapper = ({children}) => {

  const {dragStart, dragStop, dragMove} = useDrag();
  const onDragHandler = ({scrollContainer}) => (
    ev
  ) =>
    dragMove(ev, (posDiff) => {
      if (scrollContainer.current) {
        scrollContainer.current.scrollLeft += posDiff;
      }
    });

  return (
    <div onMouseLeave={dragStop} className="w-full after:content-[''] after:block after:w-full after:h-px after:bg-background">
      <ScrollMenu
        LeftArrow={LeftArrow}
        RightArrow={RightArrow}
        scrollContainerClassName="flex gap-2"
        itemClassName="first:ml-auto last:mr-auto"
        onMouseDown={() => dragStart}
        onMouseUp={() => dragStop}
        onMouseMove={onDragHandler}
      >
        {children}
      </ScrollMenu>
    </div>
  )
}

const notificationBannerFilterFn = (reminder, alertType) => {
  if (alertType === ALERT_TYPES.AsyncTask) {
    return reminder.alertType === ALERT_TYPES.AsyncTask && !reminder.isSeen;
  }

  const selectedBoard = getSavedBoardSelection();
  const canShowArchiveBanners = getIsArchivedForBoard(selectedBoard.slug);
  const isEntityArchived = reminder.entity ? reminder.entity.archived : reminder.alertType !== ALERT_TYPES.BoardAlert;
  return (canShowArchiveBanners || (!isEntityArchived && !reminder.archived)) &&
    reminder.alertType === alertType &&
    reminder.reminderState === REMINDER_STATE.NOT_SEEN;
}

const getMineNotificationBanners = (alertType, boardUserAllNotifications) => {
  return boardUserAllNotifications.filter((reminder) => notificationBannerFilterFn(reminder, alertType));
};

const getAllNotificationBanners = (alertType, reminderStore) => {
  return reminderStore.query((reminder) => notificationBannerFilterFn(reminder, alertType), true);
};

const sortBanners = (banners) => {
  return banners.sort((remA, remB) => {
    const isRemAAsync = remA.alertType === ALERT_TYPES.AsyncTask;
    const isRemBAsync = remB.alertType === ALERT_TYPES.AsyncTask;

    if (isRemAAsync && !isRemBAsync) {
      return -1; // A comes first
    }
    if (!isRemAAsync && isRemBAsync) {
      return 1; // B comes first
    }

    // Both are async or both are not, sort by date
    return remB.alertDate - remA.alertDate;
  });
};

const getNotificationBanners = ({notifyPreferenceStore, reminderStore, boardUsersStore, userId}) => {
  const currentBoardUser = boardUsersStore.find((boardUser) => boardUser.userId === userId);
  const boardUserAllNotifications = currentBoardUser.alertRecipients.reduce((accum, recipient) => {
    if (recipient.alertRecord) {
      accum.push(recipient.alertRecord);
    }
    return accum;
  }, []);

  const allNotifyPrefs = [{
    notifyType: 12,
    bannerNotify: NOTIFICATION_BANNER_PREF_CODE.MyBannersOnly,
  }, ...notifyPreferenceStore.records];
  const allBanners = allNotifyPrefs.reduce((accum, notifyPref) => {
    if (notifyPref.bannerNotify === NOTIFICATION_BANNER_PREF_CODE.MyBannersOnly) {
      accum.push(...getMineNotificationBanners(notifyPref.notifyType, boardUserAllNotifications));
    } else if (notifyPref.bannerNotify === NOTIFICATION_BANNER_PREF_CODE.AllBanners) {
      accum.push(...getAllNotificationBanners(notifyPref.notifyType, reminderStore));
    }

    return accum;
  }, []);

  return sortBanners(allBanners);
}

const AlertNotificationBanner = ({project, editorHandler, user, tasks, isBoardIndependentView = false, onCancelTask, onMarkTaskSeen}) => {
  const [alerts, setAlerts] = useState([]);
  const {boardManager, viewManager, entityManager } = useContext(BryntumContext);
  // why this state? the issue was when removed multiple items at the same time (for instance: through alert preference in settings)
  // those items still being displayed even thought that don't exist in an `alerts` array, and the issue is coming from
  // TransitionGroup, CSSTransition and ScrollMenuWrapper as ScrollMenuWrapper doesn't work properly with CSSTransition
  const [redisplay, setRedisplay] = useState(true);
  const { reminderStore, boardUsersStore, notifyPreferenceStore } = project || {};

  const getTriggeredAlerts = useCallback(() => {
    if (!reminderStore || !boardUsersStore || !notifyPreferenceStore || !user) return [];
    return getNotificationBanners({ reminderStore, boardUsersStore, notifyPreferenceStore, userId: user.id });
  }, [reminderStore, boardUsersStore, notifyPreferenceStore, user]);

  useEffect(() => {
    if (isBoardIndependentView) {
      setAlerts(tasks || []);
      return;
    }

    setAlerts(getTriggeredAlerts());
    const listener = reminderStore.on("change", () => {
      setAlerts(getTriggeredAlerts());
    });

    const notifyPreferenceDestroyListener = notifyPreferenceStore.on("change", () => {
      setRedisplay(false)
      setAlerts(getTriggeredAlerts());
      setRedisplay(true);
    });

    return () => {
      listener && listener();
      notifyPreferenceDestroyListener && notifyPreferenceDestroyListener();
    }
  }, [isBoardIndependentView, tasks, getTriggeredAlerts, reminderStore]);

  if (!redisplay) return <></>;

  const onTaskDoubleClick = (alertItem) => {
    processCompletedTask({ alertItem, project, editorHandler, boardManager, user, isBoardIndependentView })
  }

  return (
    <>
      <CSSTransition
        in={alerts.length > 0}
        timeout={300}
        classNames="alert-notification-banner-transition"
        unmountOnExit
      >
        <div className="flex items-center min-h-12 px-5 bg-muted/40 border-solid border-0 border-b border-b-border gap-2">
          <TransitionGroup component={ScrollMenuWrapper}>
            {alerts.map((alertItem) => (
              <CSSTransition
                key={alertItem.id}
                timeout={300}
                classNames="alert-notification-banner-transition-item"
              >
                <AlertNotificationBannerItem
                  alertItem={alertItem}
                  editorHandler={editorHandler}
                  viewManager={viewManager}
                  entityManager={entityManager}
                  onToggleBell={() => {
                    if (isBoardIndependentView) {
                      onMarkTaskSeen(alertItem.id);
                    } else if (user.can(GRANTS.grant_four)) {
                      alertItem.updateSeenStatus();
                    }
                  }}
                  onCancelTask={async (alertItem) => {
                    try {
                      const id = isBoardIndependentView ? alertItem.id : alertItem.asyncTask;
                      await markAsyncTaskAsSeen(id);
                      await asyncTaskCancel(id);
                      if (onCancelTask) {
                        onCancelTask(alertItem.id);
                      }
                      if (!isBoardIndependentView) {
                        alertItem.updateSeenStatus();
                      }
                    } catch (e) {
                      // handle error
                    }
                  }}
                  onTaskDoubleClick= {() => {onTaskDoubleClick(alertItem, isBoardIndependentView, project, editorHandler, boardManager, user)}}
                  isAsyncTask={isBoardIndependentView || alertItem.asyncTask}
                />
              </CSSTransition>
            ))}
          </TransitionGroup>
        </div>
      </CSSTransition>
    </>
  );
};

export default AlertNotificationBanner;
